import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDocument } from '../hooks/useDocument';
import {
  validateDocument,
  triggerExtraction,
  getDocumentExtractions,
  createVersion,
} from '../services/api';
import { useDocumentFileUrl } from '../hooks/useDocumentFileUrl';
import { useDebouncedDraftSave } from '../hooks/useDebouncedDraftSave';
import { useExtractionQueuePause } from '../hooks/useExtractionQueuePause';
import ReextractModal from '../components/documents/ReextractModal';
import ConfirmModal from '../components/ui/ConfirmModal';
import VersionSelector from '../components/documents/VersionSelector';
import CompareVersionsView from '../components/documents/CompareVersionsView';
import type { Extraction } from '../types/extraction';
import { usePolling } from '../contexts/PollingContext';
import { DynamicFormLoader } from '../components/forms/DynamicFormLoader';
import { PdfViewer, type HighlightTarget } from '../components/ui/PdfViewer';
import ExportButtons from '../components/ExportButtons';
import { ModelConfigSection } from '../components/document/ModelConfigSection';
import { useAuth } from '../contexts/AuthContext';
import {
  acquireLock,
  releaseLock,
  extendLock,
  LockConflictError,
  isNoExtraction,
} from '../services/lockService';
import { LockHeaderBanner } from '../components/documents/LockHeaderBanner';
import { FlagBadgeList } from '../components/FlagBadge';
import { ConfidenceScoreBadge } from '../components/ConfidenceScoreBadge';
import { FieldConfidencePanel } from '../components/FieldConfidencePanel';

export interface LockState {
  isHolder: boolean;
  lockedByEmail?: string;
  expiresAt?: string;
  error?: boolean;
}

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAnalyste, user } = useAuth();
  const [lockInfo, setLockInfo] = useState<LockState | null>(null);
  const isHolderRef = useRef(false);
  const lastActivityRef = useRef<number>(Date.now());
  const { disablePolling, enablePolling } = usePolling();
  const { document, extraction, provenance, provenanceFragments, isLoading, error, refetch, loadProvenance } = useDocument(id);

  const [formData, setFormData] = useState<any>(null);
  const [originalData, setOriginalData] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  // URL de streaming du PDF (jeton ephemere auto-renouvele, cf. hook).
  const pdfUrl = useDocumentFileUrl(id);
  const [highlight, setHighlight] = useState<HighlightTarget | undefined>();

  // PROD1.4-06 — réextraction
  const [reextractOpen, setReextractOpen] = useState(false);
  const [extractions, setExtractions] = useState<Extraction[]>([]);
  const [selectedExtractionId, setSelectedExtractionId] = useState<string | undefined>();
  // PROD1.4.6 #474bis — cross-user draft guard.
  const [foreignDraft, setForeignDraft] = useState<{
    payload: any;
    byId: string;
    at: string | null;
  } | null>(null);
  const [forceOverwriteDraft, setForceOverwriteDraft] = useState(false);
  // PROD1.4.6 hotfix — pause queue interactive : avertir l'analyste avant le clic.
  const extractionQueuePaused = useExtractionQueuePause();
  const [showCompare, setShowCompare] = useState(false);
  // PROD1.10 #667 — réouverture d'un doc validé en lecture seule (création v+1 éditable).
  const [reopenOpen, setReopenOpen] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  // Resizable divider
  const [leftWidthPercent, setLeftWidthPercent] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Disable polling when viewing a document detail page
  useEffect(() => {
    disablePolling();
    return () => {
      enablePolling();
    };
  }, [disablePolling, enablePolling]);

  // Auto-acquire validation lock on mount, release on unmount. Backend
  // returns 200 if we become (or remain) holder, 409 with detail.locked_by
  // if another user owns it. Cleanup is best-effort.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    acquireLock(id)
      .then((info) => {
        if (cancelled) return;
        if (isNoExtraction(info)) {
          // PROD1.4.6 hotfix : doc sans extraction courante (uploaded sans
          // /process). Pas de lock à acquérir, on n'est pas holder — l'UI
          // est rendue en lecture seule mais sans signal d'erreur.
          isHolderRef.current = false;
          setLockInfo({ isHolder: false });
          return;
        }
        isHolderRef.current = true;
        setLockInfo({
          isHolder: true,
          expiresAt: info.lock_expires_at,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        isHolderRef.current = false;
        if (err instanceof LockConflictError) {
          setLockInfo({
            isHolder: false,
            lockedByEmail: err.conflict.locked_by?.email ?? undefined,
            expiresAt: err.conflict.lock_expires_at ?? undefined,
          });
        } else {
          console.error('Lock acquire failed:', err);
          setLockInfo({ isHolder: false, error: true });
        }
      });

    return () => {
      cancelled = true;
      // Only DELETE when we actually hold the lock, to avoid a redundant
      // 403 round-trip on React strict-mode double-cleanup or after the
      // user has explicitly released it via the banner.
      if (!isHolderRef.current) return;
      isHolderRef.current = false;
      releaseLock(id).catch(() => {
        /* best-effort */
      });
    };
  }, [id]);

  // Auto-extend the lock while the analyst is actively editing.
  // Activity = any keystroke / mouse move / scroll / click on the page.
  // Tick every 5 min: if we are still the holder AND there was activity
  // within the last 5 min → silently extend. Otherwise let the TTL expire
  // so an idle / offline analyst doesn't hold the lock for nothing.
  useEffect(() => {
    if (!id) return;

    const bump = () => {
      lastActivityRef.current = Date.now();
    };
    const events: Array<keyof DocumentEventMap> = ['keydown', 'mousemove', 'scroll', 'click'];
    for (const evt of events) {
      window.document.addEventListener(evt, bump, { passive: true });
    }

    const FIVE_MIN_MS = 5 * 60 * 1000;
    const intervalId = window.setInterval(async () => {
      if (!isHolderRef.current) return;
      if (Date.now() - lastActivityRef.current > FIVE_MIN_MS) return;
      try {
        const info = await extendLock(id);
        setLockInfo((prev) =>
          prev && prev.isHolder ? { ...prev, expiresAt: info.lock_expires_at } : prev,
        );
      } catch {
        /* best-effort — next tick will retry or the TTL will expire */
      }
    }, FIVE_MIN_MS);

    return () => {
      window.clearInterval(intervalId);
      for (const evt of events) {
        window.document.removeEventListener(evt, bump);
      }
    };
  }, [id]);

  // Extraction effectivement affichée : version sélectionnée par l'utilisateur,
  // sinon la current. Le payload du formulaire suit cette sélection.
  const displayedExtraction = (
    selectedExtractionId
      ? extractions.find((e) => e.id === selectedExtractionId)
      : null
  ) ?? extraction;

  const isViewingOlderVersion =
    !!displayedExtraction && displayedExtraction.is_current === false;

  // PROD1.10 #667 — un document validé est en lecture seule tant qu'on consulte
  // sa version courante. Pour corriger, l'analyste doit explicitement « Rouvrir »
  // (crée une v+1 éditable) — garde-fou anti-modification accidentelle.
  const isValidatedReadOnly =
    document?.status === 'validated' && !isViewingOlderVersion;

  // PROD1.9 — la provenance (et donc la surbrillance PDF) doit suivre
  // l'extraction RÉELLEMENT affichée, pas seulement la current. Recharge la
  // provenance quand on change de version et efface la surbrillance courante.
  useEffect(() => {
    loadProvenance(displayedExtraction?.id);
    setHighlight(undefined);
  }, [displayedExtraction?.id, loadProvenance]);

  // Load extraction data into form when it becomes available / version changes.
  // PROD1.4.6 #474 — if a draft exists for the current user, restore it instead
  // of the validated payload, and notify via toast. Another user's draft is
  // silently ignored.
  useEffect(() => {
    setForeignDraft(null);
    setForceOverwriteDraft(false);
    if (displayedExtraction?.payload_json) {
      const draft = (displayedExtraction as any).draft_payload_json;
      const draftBy = (displayedExtraction as any).draft_updated_by_id;
      const draftAt = (displayedExtraction as any).draft_updated_at;
      const isCurrent = displayedExtraction.is_current !== false;

      if (draft && user?.id && draftBy === user.id && isCurrent) {
        // Own draft → restore.
        setFormData(draft);
        setOriginalData(displayedExtraction.payload_json);
        const when = draftAt
          ? new Date(draftAt).toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '';
        toast.success(`Brouillon restauré${when ? ` (modifié le ${when})` : ''}`);
      } else if (draft && user?.id && draftBy && draftBy !== user.id && isCurrent) {
        // Foreign draft → show banner, render validated payload by default
        // (auto-save blocked until user clicks "Écraser et travailler dessus").
        setForeignDraft({ payload: draft, byId: draftBy, at: draftAt ?? null });
        setFormData(displayedExtraction.payload_json);
        setOriginalData(displayedExtraction.payload_json);
      } else {
        setFormData(displayedExtraction.payload_json);
        setOriginalData(displayedExtraction.payload_json);
      }
    } else {
      setFormData(null);
      setOriginalData(null);
    }
  }, [displayedExtraction, user?.id]);

  // Auto-save brouillon (PROD1.4.6 #474) : débounced 1500ms, holder uniquement,
  // pas de save si on consulte une version historique.
  const draftEnabled =
    !!lockInfo?.isHolder &&
    !isViewingOlderVersion &&
    !isValidatedReadOnly &&
    displayedExtraction?.id === extraction?.id &&
    (!foreignDraft || forceOverwriteDraft);
  const { lastSavedAt: draftSavedAt, conflict: draftConflict } = useDebouncedDraftSave(
    id,
    formData,
    !!lockInfo?.isHolder,
    draftEnabled,
    forceOverwriteDraft,
  );

  // Si le serveur signale un conflit (race entre 2 onglets), on remonte
  // la même bannière que pour un foreign draft détecté à l'ouverture.
  useEffect(() => {
    if (draftConflict && !foreignDraft) {
      setForeignDraft({
        payload: null,
        byId: draftConflict.draft_updated_by_id,
        at: draftConflict.draft_updated_at,
      });
    }
  }, [draftConflict, foreignDraft]);

  // PDF charge en streaming via useDocumentFileUrl(id) (jeton ephemere
  // auto-renouvele) — plus de telechargement blob complet ni de revoke manuel.

  // Auto-refresh when document is processing
  useEffect(() => {
    if (document?.status === 'processing') {
      const interval = setInterval(() => {
        refetch();
      }, 3000); // Refresh every 3 seconds

      return () => clearInterval(interval);
    }
  }, [document?.status, refetch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id || !formData) {
      toast.error('Données manquantes');
      return;
    }

    if (lockInfo && !lockInfo.isHolder) {
      toast.error('Document verrouillé par un autre utilisateur');
      return;
    }

    setIsSubmitting(true);

    try {
      await validateDocument(id, formData, extraction?.id ?? null);
      toast.success('Document validé avec succès');
      refetch();
    } catch (err: any) {
      // PROD1.4.6 #473 — race version v1/v2
      const detail = err?.detail ?? err?.body?.detail;
      const code = typeof detail === 'object' ? detail?.code : undefined;
      if (code === 'extraction_version_changed') {
        toast.error(
          'La version courante a changé pendant votre saisie. Rechargez le document.',
        );
      } else {
        toast.error(err.message || 'Erreur lors de la validation');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setFormData({ ...originalData });
      toast.success('Modifications annulées');
    }
  };

  const refreshExtractions = useCallback(async () => {
    if (!id) return;
    try {
      const list = await getDocumentExtractions(id);
      setExtractions(list);
    } catch {
      setExtractions([]);
    }
  }, [id]);

  useEffect(() => {
    refreshExtractions();
  }, [refreshExtractions, document?.status]);

  const handleReextractConfirmed = async () => {
    setReextractOpen(false);
    await refreshExtractions();
    refetch();
    setSelectedExtractionId(undefined); // bascule vers la nouvelle current
    toast.success('Bascule sur la nouvelle version');
  };

  const handleVersionChange = async (extractionId: string) => {
    // PROD1.10 — recharger la liste AVANT de basculer : `extractions[]` est un
    // snapshot figé au montage (rechargé seulement quand `document.status`
    // change), donc il ne contient pas le `draft_payload_json` fraîchement
    // auto-sauvé. Sans ce refresh, revenir sur la version courante via le menu
    // réafficherait le payload d'origine et perdrait le brouillon à l'écran
    // (alors qu'il est bien persisté côté serveur). `getDocumentExtractions`
    // renvoie déjà les champs draft → l'effet de restauration les retrouve.
    await refreshExtractions();
    setSelectedExtractionId(extractionId);
  };

  // PROD1.10 #667 — réouverture : crée une v+1 éditable (source=human, sans LLM),
  // puis recharge le document et l'historique et bascule sur la nouvelle current.
  const handleReopenConfirmed = async () => {
    if (!id || isReopening) return;
    setIsReopening(true);
    try {
      await createVersion(id);
      await refreshExtractions();
      refetch();
      setSelectedExtractionId(undefined); // bascule vers la nouvelle current
      setReopenOpen(false);
      toast.success('Nouvelle version éditable créée');
    } catch (err: any) {
      const detail = err?.detail ?? err?.body?.detail;
      const code = typeof detail === 'object' ? detail?.code : undefined;
      if (code === 'extraction_locked') {
        toast.error('Document verrouillé par un autre utilisateur');
      } else if (code === 'extraction_in_progress') {
        toast.error('Extraction en cours, réessayez dans un instant');
      } else {
        toast.error(err?.message || 'Erreur lors de la réouverture');
      }
    } finally {
      setIsReopening(false);
    }
  };

  const v1ForCompare = extractions.find(
    (e) => e.id !== (selectedExtractionId ?? extractions.find((x) => x.is_current)?.id)
  );
  const v2ForCompare = extractions.find(
    (e) => e.id === (selectedExtractionId ?? extractions.find((x) => x.is_current)?.id)
  );

  const handleTriggerExtraction = async () => {
    if (!id) return;

    setIsExtracting(true);
    try {
      await triggerExtraction(id);
      toast.success('Extraction LLM déclenchée');
      // Refetch document to update status
      setTimeout(() => refetch(), 2000);
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const detail = err?.response?.data?.detail ?? err?.detail;
      if (status === 503 && detail?.code === 'extraction_queue_paused') {
        // Le bandeau apparaîtra au prochain tick (max 30s) ; toast immédiat
        // pour le feedback utilisateur.
        toast.error('Extractions temporairement en pause. Réessayez dans quelques minutes.');
      } else {
        toast.error(err.message || 'Erreur lors du déclenchement de l\'extraction');
      }
    } finally {
      setIsExtracting(false);
    }
  };

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    window.document.body.style.userSelect = 'none';
    window.document.body.style.cursor = 'col-resize';

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const percent = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setLeftWidthPercent(Math.min(70, Math.max(20, percent)));
    };

    const onMouseUp = () => {
      setDragging(false);
      window.document.body.style.userSelect = '';
      window.document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleExtendLock = async () => {
    if (!id) return;
    try {
      const info = await extendLock(id);
      setLockInfo((prev) =>
        prev ? { ...prev, expiresAt: info.lock_expires_at } : prev,
      );
      toast.success('Verrou prolongé');
    } catch (err: any) {
      toast.error(err?.message || 'Impossible de prolonger le verrou');
    }
  };

  const handleReleaseLock = async () => {
    if (!id) return;
    try {
      await releaseLock(id);
      isHolderRef.current = false;
      setLockInfo((prev) =>
        prev ? { ...prev, isHolder: false } : prev,
      );
      toast.success('Verrou libéré');
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la libération du verrou');
    }
  };

  const handleProvenanceClick = (fieldPath: string) => {
    // Tous les fragments du champ → un rectangle par bbox. Fallback sur la vue
    // représentative si la liste complète n'est pas disponible.
    const frags = provenanceFragments?.[fieldPath];
    if (frags && frags.length > 0) {
      setHighlight({ items: frags.map((f) => ({ page: f.page, snippet: f.snippet, bbox: f.bbox })) });
      return;
    }
    const prov = provenance?.[fieldPath];
    if (prov) {
      setHighlight({ items: [{ page: prov.page, snippet: prov.snippet, bbox: prov.bbox }] });
    }
  };

  const handleClearHighlight = () => {
    setHighlight(undefined);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-4">Chargement...</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            ← Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">Erreur: {error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-600">Document non trouvé</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">

      {extractionQueuePaused && (
        <div
          role="alert"
          className="bg-yellow-50 border-b border-yellow-300 px-6 py-2 text-sm text-yellow-900 flex items-center gap-2"
        >
          <span className="font-medium">⏸ Extractions temporairement en pause</span>
          <span className="text-xs text-yellow-700">
            Les actions "Extraire" et "Ré-extraire" sont indisponibles pour le moment.
          </span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-800"
          >
            ← Retour
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {document.original_filename}
            </h1>
            <p className="text-sm text-gray-500">
              Domaine: {document.domain || 'Non spécifié'}
            </p>
            {/* Versions de config (schema/prompt/llm/ocr) de la version
                AFFICHÉE. Le backend renseigne désormais ces `*_version`
                dérivées sur toutes les versions de l'historique
                (build_extraction_response), donc le bandeau suit la version
                sélectionnée comme le reste de la page. */}
            {displayedExtraction && (displayedExtraction.schema_version || displayedExtraction.prompt_version || displayedExtraction.llm_config_version || displayedExtraction.ocr_config_version) && (
              <p className="text-sm text-gray-500">
                {[
                  displayedExtraction.schema_version && `Schema : v${displayedExtraction.schema_version}`,
                  displayedExtraction.prompt_version && `Prompt : v${displayedExtraction.prompt_version}`,
                  displayedExtraction.llm_config_version && `LLM : v${displayedExtraction.llm_config_version}`,
                  displayedExtraction.ocr_config_version && `OCR : v${displayedExtraction.ocr_config_version}`,
                ].filter(Boolean).join(' | ')}
              </p>
            )}
            {/* PROD1.7.8 N2 — flags détectés par le LLM (cas complexes) */}
            {displayedExtraction?.flags && displayedExtraction.flags.length > 0 && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-amber-700">
                  ⚠ Document complexe :
                </span>
                <FlagBadgeList flags={displayedExtraction.flags} />
              </div>
            )}
            {/* PROD1.7.8 N3 — score global confidence (suit la version affichée) */}
            {displayedExtraction && (displayedExtraction.confidence_score !== null || displayedExtraction.field_confidences) && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <ConfidenceScoreBadge score={displayedExtraction.confidence_score} verbose />
              </div>
            )}
            <FieldConfidencePanel fieldConfidences={displayedExtraction?.field_confidences} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {extractions.length > 0 && (
            <VersionSelector
              extractions={extractions}
              selectedId={selectedExtractionId}
              onVersionChange={handleVersionChange}
            />
          )}
          {extractions.length >= 2 && (
            <button
              onClick={() => setShowCompare((v) => !v)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {showCompare ? 'Masquer comparaison' : 'Comparer versions'}
            </button>
          )}
          {['extracted', 'validated'].includes(document.status) && (
            <button
              onClick={() => setReextractOpen(true)}
              disabled={isExtracting || extractionQueuePaused || (lockInfo !== null && !lockInfo.isHolder)}
              title={
                extractionQueuePaused
                  ? 'Extractions temporairement en pause'
                  : lockInfo && !lockInfo.isHolder
                  ? 'Verrouillé par un autre utilisateur'
                  : undefined
              }
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
            >
              🔁 Ré-extraire
            </button>
          )}
          {['uploaded', 'error'].includes(document.status) && (
            <button
              onClick={handleTriggerExtraction}
              disabled={isExtracting || extractionQueuePaused || (lockInfo !== null && !lockInfo.isHolder)}
              title={extractionQueuePaused ? 'Extractions temporairement en pause' : undefined}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
            >
              {isExtracting ? 'En cours...' : '🤖 Extraire'}
            </button>
          )}
          <ExportButtons documentId={document.id} status={document.status} />
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              document.status === 'validated'
                ? 'bg-green-100 text-green-800'
                : document.status === 'extracted'
                ? 'bg-blue-100 text-blue-800'
                : document.status === 'processing'
                ? 'bg-yellow-100 text-yellow-800'
                : document.status === 'error'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {document.status}
          </span>
        </div>
      </div>

      {lockInfo && lockInfo.expiresAt && !lockInfo.error && (
        <LockHeaderBanner
          isHolder={lockInfo.isHolder}
          lockedByEmail={lockInfo.lockedByEmail}
          expiresAt={lockInfo.expiresAt}
          onExtend={lockInfo.isHolder ? handleExtendLock : undefined}
          onRelease={lockInfo.isHolder ? handleReleaseLock : undefined}
        />
      )}

      {showCompare && v1ForCompare && v2ForCompare && (
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <CompareVersionsView
            v1Label={`v${v1ForCompare.version} ${v1ForCompare.validated_at ? '(validée)' : ''}`}
            v2Label={`v${v2ForCompare.version} ${v2ForCompare.is_current ? '(courante)' : ''}`}
            v1Payload={v1ForCompare.payload_json}
            v2Payload={v2ForCompare.payload_json}
          />
        </div>
      )}

      <ReextractModal
        open={reextractOpen}
        documentId={id || ''}
        domain={document.domain || ''}
        validatedAt={extraction?.validated_at}
        validatedByName={null}
        onCancel={() => setReextractOpen(false)}
        onConfirmed={handleReextractConfirmed}
      />

      {/* PROD1.10 #667 — confirmation avant de rouvrir un doc validé. */}
      <ConfirmModal
        open={reopenOpen}
        title="Rouvrir pour corriger"
        message="Créer une nouvelle version éditable ? La version validée actuelle sera conservée dans l'historique."
        confirmLabel={isReopening ? 'En cours...' : 'Rouvrir'}
        cancelLabel="Annuler"
        onCancel={() => setReopenOpen(false)}
        onConfirm={handleReopenConfirmed}
      />

      {/* Main Content — Two columns with resizable divider */}
      <div ref={containerRef} className="flex flex-1 overflow-hidden">
        {/* PDF Viewer - Left Side */}
        <div
          className="bg-gray-50 overflow-hidden flex flex-col"
          style={{ width: `${leftWidthPercent}%`, pointerEvents: dragging ? 'none' : undefined }}
        >
          {pdfUrl ? (
            <PdfViewer url={pdfUrl} filename={document.original_filename} highlight={highlight} onClearHighlight={handleClearHighlight} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Chargement du PDF...</p>
            </div>
          )}
        </div>

        {/* Resizable divider handle */}
        <div
          onMouseDown={handleDividerMouseDown}
          className="w-1.5 bg-gray-200 hover:bg-blue-400 cursor-col-resize flex-shrink-0 transition-colors"
        />

        {/* Form - Right Side */}
        <div className="bg-white overflow-auto" style={{ width: `${100 - leftWidthPercent}%` }}>
          <div className="p-6">
            {!displayedExtraction ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mb-4">
                  Aucune extraction disponible pour ce document
                </p>
                {document.status === 'processing' && (
                  <p className="text-sm text-gray-500">
                    Extraction en cours...
                  </p>
                )}
              </div>
            ) : (
              <>
                {isViewingOlderVersion && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
                    Vous consultez la version <b>v{displayedExtraction.version}</b> (lecture seule).
                    Sélectionnez la version courante dans la liste pour modifier ou valider.
                  </div>
                )}
                {/* PROD1.10 #667 — doc validé en lecture seule : invite à « Rouvrir ». */}
                {isValidatedReadOnly && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800 flex items-center justify-between gap-3">
                    <span>
                      Ce document est <b>validé</b> et en lecture seule.
                      Pour le corriger, créez une nouvelle version éditable.
                    </span>
                    <button
                      type="button"
                      onClick={() => setReopenOpen(true)}
                      disabled={isReopening || (lockInfo !== null && !lockInfo.isHolder)}
                      title={
                        lockInfo && !lockInfo.isHolder
                          ? 'Verrouillé par un autre utilisateur'
                          : undefined
                      }
                      className="shrink-0 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                    >
                      🔓 Rouvrir pour corriger
                    </button>
                  </div>
                )}
                {foreignDraft && !forceOverwriteDraft && (
                  <div
                    role="alert"
                    className="mb-4 p-3 bg-orange-50 border border-orange-300 rounded-md text-sm text-orange-900"
                  >
                    <div className="font-medium mb-1">
                      ⚠ Un brouillon non validé existe pour cette version
                    </div>
                    <div className="text-xs mb-2">
                      Modifié par un autre utilisateur
                      {foreignDraft.at
                        ? ` le ${new Date(foreignDraft.at).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}`
                        : ''}
                      . Vos modifications NE sont PAS sauvegardées tant que vous ne décidez pas.
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {foreignDraft.payload && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(foreignDraft.payload);
                            toast('Brouillon de l\'autre utilisateur affiché (lecture seule).');
                          }}
                          className="px-3 py-1 text-xs border border-orange-400 rounded hover:bg-orange-100"
                        >
                          Voir le brouillon
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            !confirm(
                              'Écraser le brouillon de l\'autre utilisateur ? Ses saisies non validées seront perdues.',
                            )
                          )
                            return;
                          setForceOverwriteDraft(true);
                          setForeignDraft(null);
                          toast.success('Brouillon repris — auto-save réactivé.');
                        }}
                        className="px-3 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        Écraser et travailler dessus
                      </button>
                    </div>
                  </div>
                )}
                <fieldset disabled={isViewingOlderVersion || isValidatedReadOnly || (lockInfo !== null && !lockInfo.isHolder)} className="border-0 p-0 m-0">
                  <DynamicFormLoader
                    domain={document.domain}
                    schemaId={displayedExtraction.schema_id ?? undefined}
                    data={formData || {}}
                    provenance={provenance}
                    fieldConfidences={displayedExtraction.field_confidences}
                    onChange={setFormData}
                    onSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    onProvenanceClick={handleProvenanceClick}
                  />
                </fieldset>
              </>
            )}

            {displayedExtraction && (
              <>
                {!isViewingOlderVersion && !isValidatedReadOnly && draftEnabled && draftSavedAt && (
                  <div className="mt-3 text-xs text-gray-500" data-testid="draft-saved-indicator">
                    ◐ Brouillon sauvegardé {draftSavedAt.toLocaleTimeString('fr-FR')}
                  </div>
                )}
                {!isViewingOlderVersion && !isValidatedReadOnly && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSubmitting || (lockInfo !== null && !lockInfo.isHolder)}
                        className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Annuler les modifications
                      </button>
                    </div>
                  </div>
                )}

                {/* Model Configuration Section */}
                {!isAnalyste() && <ModelConfigSection extraction={displayedExtraction} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
